import type { FC } from 'react';
import { SetStateAction, useState } from 'react';
import { useHistory } from 'react-router';
import { Link, useParams } from 'react-router-dom';
import { NetworkStatus } from '@apollo/client';
import { Center, HStack, VStack } from '@chakra-ui/react';

import { TeamMembersRequestStatus } from '__generated__/GQL';
import flyingBill from 'assets/brand/flying-bills.png';
import useRequestsUpdatedSubscription from 'common/hooks/useTeamRequestsUpdatedSubscription';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import * as Layout from 'layouts/ContentLayout';

import { useManagedUserBankTransferRequests } from '../../hooks/useManagedUserBankTransferRequests';
import { ManagedUserBankTransferRequestsDocument } from '../../hooks/useManagedUserBankTransferRequests/managedUserBankTransferRequests.gql';
import * as routes from '../../routes';
import { RootPathParams } from '../../routes';
import BankTransferRequestSidebar from './components/BankTransferRequestSidebar';
import BankTransferRequestsList from './components/BankTransferRequestsList';
import EmptyView from './components/EmptyView';
import locales from './locales';

const Home: FC = () => {
  const { bankTransferRequestId } = useParams<RootPathParams>();
  const [status, setStatus] = useState<TeamMembersRequestStatus[]>([
    TeamMembersRequestStatus.Pending,
  ]);
  const history = useHistory();
  const {
    bankTransferRequests,
    error,
    loading,
    loadMore,
    networkStatus,
    pageInfo,
    refetch,
  } = useManagedUserBankTransferRequests(status);

  useRequestsUpdatedSubscription({
    onData: ({ client, data }) => {
      if (
        data.data &&
        data.data.teamMembersRequestsUpdated.status !==
          TeamMembersRequestStatus.Canceled
      ) {
        // If this a canceled request, we don't need to refetch the list as it is
        // already removed from the list within the Apollo cache because it is
        // only the employee who can cancel its request.
        client.refetchQueries({
          include: [ManagedUserBankTransferRequestsDocument],
        });
      }
    },
  });

  const handleOnStatusChange = (
    value: SetStateAction<TeamMembersRequestStatus[]>,
  ): void => {
    history.replace(routes.root);
    setStatus(value);
  };

  if (error) {
    return (
      <Center height="100%">
        <ErrorCard retry={refetch} />
      </Center>
    );
  }

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;
  const hasAnyRequests = pageInfo ? pageInfo.totalCountInCompany >= 1 : false;

  return (
    <Layout.Root>
      <Layout.Body size="md">
        <HStack marginBottom="space-40" spacing="space-8">
          <VStack alignItems="flex-start">
            <Heading size="lg">{locales.title}</Heading>
            {hasAnyRequests ? (
              <Text variant="primary">{locales.description}</Text>
            ) : null}
          </VStack>
          <Button
            alignSelf="flex-start"
            as={Link}
            flexShrink={0}
            icon="plus"
            iconPosition="left"
            marginLeft="auto"
            to={routes.requestBankTransferPath}
            variant="secondary"
          >
            {locales.requestBankTransfer}
          </Button>
        </HStack>
        {hasAnyRequests ? (
          <>
            <BankTransferRequestsList
              isLoading={loading}
              isLoadingMore={isLoadingMore}
              onStatusChange={handleOnStatusChange}
              pageInfo={pageInfo}
              requests={bankTransferRequests}
              selectedBankTransferRequestId={bankTransferRequestId}
              status={status}
            />
            {pageInfo?.hasNextPage ? (
              <Button
                alignSelf="center"
                isLoading={isLoadingMore}
                marginTop="space-34"
                onClick={loadMore}
                variant="secondary"
              >
                {locales.loadMore}
              </Button>
            ) : null}
          </>
        ) : (
          <>
            {loading ? (
              <Loader />
            ) : (
              <EmptyView
                description={locales.noRequestsDescription}
                imageSrc={flyingBill}
                title={locales.noRequestsTitle}
              />
            )}
          </>
        )}
      </Layout.Body>
      {bankTransferRequestId ? (
        <Layout.Sidebar isScrollable>
          <BankTransferRequestSidebar requestId={bankTransferRequestId} />
        </Layout.Sidebar>
      ) : null}
    </Layout.Root>
  );
};

export default Home;
